import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

export const AdminDashboard = () => {
  const { i18n } = useTranslation();
  const { isSuperadmin } = useAuth();
  const navigate = useNavigate();
  const isGerman = i18n.language === 'de';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentChanges, setRecentChanges] = useState([]);
  const [allChanges, setAllChanges] = useState([]);
  const [changesSearch, setChangesSearch] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    country: '',
    city: '',
  });

  useEffect(() => {
    loadUsers();
    loadRecentChanges();
  }, []);

  const loadUsers = async () => {
    try {
      const params = {
        search: search || undefined,
        limit: 1000,
      };
      const response = await adminAPI.getUsers(params);
      setUsers(response.data);
    } catch (error) {
      toast.error(isGerman ? 'Fehler beim Laden der Benutzer' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentChanges = async () => {
    try {
      const response = await adminAPI.getRecentChanges(50);
      setAllChanges(response.data);
      setRecentChanges(response.data);
    } catch (error) {
      console.error('Failed to load recent changes:', error);
    }
  };

  const handleChangesSearch = (searchTerm) => {
    setChangesSearch(searchTerm);
    
    if (!searchTerm) {
      setRecentChanges(allChanges);
      return;
    }
    
    const filtered = allChanges.filter(change => {
      const searchLower = searchTerm.toLowerCase();
      return (
        change.user_name.toLowerCase().includes(searchLower) ||
        change.mpg_username.toLowerCase().includes(searchLower) ||
        change.changed_by.toLowerCase().includes(searchLower) ||
        Object.keys(change.diff).some(key => key.toLowerCase().includes(searchLower))
      );
    });
    
    setRecentChanges(filtered);
  };


  const handleExportCSV = async () => {
    try {



      const response = await adminAPI.exportUsersCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `benutzer_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(isGerman ? 'Export erfolgreich' : 'Export successful');
    } catch (error) {
      toast.error(isGerman ? 'Export fehlgeschlagen' : 'Export failed');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(isGerman 
      ? `Benutzer "${userName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden!`
      : `Really delete user "${userName}"? This action cannot be undone!`)) {
      return;
    }

    try {
      await adminAPI.deleteUser(userId);
      toast.success(isGerman ? 'Benutzer gelöscht' : 'User deleted');
      loadUsers();
      loadRecentChanges();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };


  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    setLoading(true);
    
    // Trigger search with the new term
    setTimeout(() => {
      loadUsersWithSearch(searchTerm);
    }, 300); // Debounce
  };

  const loadUsersWithSearch = async (searchTerm) => {
    try {
      const params = {
        search: searchTerm || undefined,
        limit: 1000,
      };
      const response = await adminAPI.getUsers(params);
      setUsers(response.data);
    } catch (error) {
      toast.error(isGerman ? 'Fehler beim Laden der Benutzer' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, action) => {
    try {
      await adminAPI.updateUserRole(userId, action);
      toast.success(isGerman 
        ? (action === 'add_mitarbeiter' ? 'Mitarbeiter-Rechte erteilt' : 'Mitarbeiter-Rechte entzogen')
        : (action === 'add_mitarbeiter' ? 'Employee rights granted' : 'Employee rights revoked'));
      loadUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update role';
      toast.error(message);
    }
  };

  const translateFieldName = (fieldName) => {
    const translations = {
      'house_no': isGerman ? 'Hausnummer' : 'House Number',
      'street': isGerman ? 'Straße' : 'Street',
      'zip': isGerman ? 'Postleitzahl' : 'ZIP',
      'city': isGerman ? 'Stadt' : 'City',
      'country': isGerman ? 'Land' : 'Country',
      'first_name': isGerman ? 'Vorname' : 'First Name',
      'last_name': isGerman ? 'Nachname' : 'Last Name',
      'mpg_username': 'MPG Username',
      'sponsor_name': 'Sponsor',
      'shipping_location': isGerman ? 'Versandoption' : 'Shipping Option',
      'ship_to_friend_username': isGerman ? 'Versand an Freund' : 'Ship to Friend',
      'Vorname': isGerman ? 'Vorname' : 'First Name',
      'Nachname': isGerman ? 'Nachname' : 'Last Name',
      'Versandoption': isGerman ? 'Versandoption' : 'Shipping Option',
      'Versand an Freund': isGerman ? 'Versand an Freund' : 'Ship to Friend',
    };
    
    return translations[fieldName] || fieldName;
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-container">
        <h1>{isGerman ? 'Admin Dashboard' : 'Admin Dashboard'}</h1>

        {/* Superadmin: Export Button */}
        {isSuperadmin() && (
          <div style={{ marginBottom: '1.5rem', textAlign: 'right' }}>
            <button 
              onClick={handleExportCSV}
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: 'white',
                padding: '0.875rem 2rem',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: '700',
                fontSize: '1rem',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}
            >
              📊 {isGerman ? 'Benutzerdaten als Excel exportieren' : 'Export Users to Excel'}
            </button>
          </div>
        )}

        {/* Recent Changes - Table Format */}
        <div className="recent-changes-card">
          <h2>{isGerman ? '📝 Letzte Änderungen' : '📝 Recent Changes'}</h2>
          
          {/* Search for changes */}
          <div className="filters-card" style={{ marginBottom: '1.5rem' }}>
            <div className="filter-group" style={{ flex: 1 }}>
              <input
                type="text"
                placeholder={isGerman ? 'Änderungen durchsuchen...' : 'Search changes...'}
                value={changesSearch}
                onChange={(e) => handleChangesSearch(e.target.value)}
                data-testid="changes-search-input"
              />
            </div>
          </div>
          
          <div className="admin-changes-table-container" style={{
            maxHeight: '660px',
            overflowY: 'auto',
            overflowX: 'auto',
            border: '2px solid #E5E7EB',
            borderRadius: '0.5rem',
            background: '#FFFFFF'
          }}>
            {recentChanges.length === 0 ? (
              <p className="no-changes">{isGerman ? 'Keine Änderungen gefunden' : 'No changes found'}</p>
            ) : (
              <table className="admin-table-white" style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: '#FFFFFF'
              }}>
                <thead style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#F3F4F6',
                  zIndex: 10
                }}>
                  <tr>
                    <th style={{ width: '180px', padding: '1rem', textAlign: 'left', backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                      {isGerman ? 'Zeit' : 'Time'}
                    </th>
                    <th style={{ width: '220px', padding: '1rem', textAlign: 'left', backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                      {isGerman ? 'Benutzer' : 'User'}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                      {isGerman ? 'Änderungen' : 'Changes'}
                    </th>
                    <th style={{ width: '200px', padding: '1rem', textAlign: 'left', backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                      {isGerman ? 'Geändert von' : 'Changed By'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentChanges.map((change) => (
                    <tr key={change._id} onClick={() => navigate(`/admin/user/${change.user_id}`)} 
                        style={{ cursor: 'pointer', background: '#FFFFFF' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}>
                      <td style={{ fontSize: '0.9375rem', color: '#6B7280', padding: '1rem', borderBottom: '1px solid #E5E7EB', background: 'inherit' }}>
                        {new Date(change.changed_at).toLocaleString('de-DE')}
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #E5E7EB', background: 'inherit' }}>
                        <div>
                          <strong style={{ fontSize: '1rem', color: '#4A3428' }}>{change.user_name}</strong>
                          <br />
                          <code style={{ fontSize: '0.875rem', color: '#005F6B', background: 'rgba(0,95,107,0.1)', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>
                            @{change.mpg_username}
                          </code>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #E5E7EB', background: 'inherit' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                          {Object.entries(change.diff).slice(0, 3).map(([field, diff]) => (
                            <span key={field} style={{
                              background: '#E0F2FE',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.8125rem',
                              border: '1px solid #7DD3C0',
                              whiteSpace: 'nowrap'
                            }}>
                              <strong style={{ color: '#005F6B' }}>{translateFieldName(field)}</strong>
                            </span>
                          ))}
                          {Object.keys(change.diff).length > 3 && (
                            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                              +{Object.keys(change.diff).length - 3} mehr
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#6B7280', fontStyle: 'italic', padding: '1rem', borderBottom: '1px solid #E5E7EB', background: 'inherit' }}>
                        {change.changed_by}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Users Section */}
        <h2 style={{ color: '#005F6B', marginTop: '2rem', marginBottom: '1.5rem', fontSize: '1.75rem' }}>
          {isGerman ? '📊 Übersicht Benutzerdaten' : '📊 User Data Overview'}
        </h2>

        {/* Filters - Single unified search */}
        <div className="filters-card">
          <div className="filter-group" style={{ flex: 1 }}>
            <input
              type="text"
              placeholder={isGerman ? 'Benutzer durchsuchen (Name, E-Mail, Stadt, Land, MPG Username, Sponsor)...' : 'Search users (Name, Email, City, Country, MPG Username, Sponsor)...'}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              data-testid="admin-search-input"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="users-card">
          <h2>{isGerman ? 'Benutzer gesamt' : 'Total Users'}: {users.length}</h2>

          {loading ? (
            <div className="loading">{isGerman ? 'Lädt...' : 'Loading...'}</div>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>{isGerman ? 'E-Mail' : 'Email'}</th>
                    <th>{isGerman ? 'Vorname' : 'First Name'}</th>
                    <th>{isGerman ? 'Nachname' : 'Last Name'}</th>
                    <th>MPG Username</th>
                    <th>{isGerman ? 'Rolle' : 'Role'}</th>
                    <th>{isGerman ? 'Versand an Freund' : 'Ship to Friend'}</th>
                    <th>{isGerman ? 'Stadt' : 'City'}</th>
                    <th>{isGerman ? 'Land' : 'Country'}</th>
                    <th>{isGerman ? 'Aktionen' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isSuperadminUser = user.roles?.includes('superadmin');
                    const isMitarbeiter = user.roles?.includes('mitarbeiter');
                    
                    return (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td>{user.email}</td>
                        <td>{user.first_name}</td>
                        <td>{user.last_name}</td>
                        <td><code className="mpg-username-badge">{user.mpg_username}</code></td>
                        <td>
                          {isSuperadminUser ? (
                            <span className="role-badge superadmin">Superadmin</span>
                          ) : isMitarbeiter ? (
                            <span className="role-badge mitarbeiter">Mitarbeiter</span>
                          ) : (
                            <span className="role-badge user">User</span>
                          )}
                        </td>
                        <td>
                          {user.ship_to_friend_username ? (
                            <code style={{ fontSize: '0.875rem', color: '#005F6B', background: 'rgba(0,95,107,0.1)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>
                              @{user.ship_to_friend_username}
                            </code>
                          ) : (
                            <span style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>-</span>
                          )}
                        </td>
                        <td>{user.address?.city}</td>
                        <td>{user.address?.country}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => navigate(`/admin/user/${user.id}`)}
                              className="action-btn view"
                              data-testid={`view-user-${user.id}`}
                            >
                              {isGerman ? 'Details' : 'View'}
                            </button>
                            
                            {!isSuperadminUser && (
                              isMitarbeiter ? (
                                <button
                                  onClick={() => handleRoleChange(user.id, 'remove_mitarbeiter')}
                                  className="action-btn revoke"
                                  data-testid={`revoke-${user.id}`}
                                >
                                  {isGerman ? 'Rechte entziehen' : 'Revoke Rights'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleRoleChange(user.id, 'add_mitarbeiter')}
                                  className="action-btn grant"
                                  data-testid={`grant-${user.id}`}
                                >
                                  {isGerman ? 'Mitarbeiter machen' : 'Make Employee'}
                                </button>
                              )
                            )}
                            
                            {isSuperadmin() && !isSuperadminUser && (
                              <button
                                onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                                className="action-btn"
                                style={{
                                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                                  color: 'white'
                                }}
                                data-testid={`delete-${user.id}`}
                              >
                                🗑️ {isGerman ? 'Löschen' : 'Delete'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
